import { Box, Divider, Group } from '@byecode/ui'
import type { ClickTriggerConfigure, FlowNode, LoopRecordsConfig, VariableTriggerConfigure } from '@lighthouse/shared'
import { OUTPUT_SINGLE_DATA_NODES } from '@lighthouse/shared'
import { useAtomValue } from 'jotai'
import { find } from 'rambda'
import React, { useMemo } from 'react'

import { workflowListAtom } from '@/atoms/workflow/state'

import * as SC from '../../styles'
import { SettingSubProcessContent } from '../component/SettingSubProcessContent'
import { SubProcessSelector } from '../component/SubProcessSelector'

interface RecordsConfigProps {
    allParentNodes: FlowNode[]
    nodeName: string
    value?: Partial<LoopRecordsConfig>
    onChange?: (value: Partial<LoopRecordsConfig>) => void
}

export const RecordsConfig: React.FC<RecordsConfigProps> = ({ allParentNodes, nodeName, value, onChange }) => {
    const workflows = useAtomValue(workflowListAtom)
    const subProcessId = value?.subProcessId
    const nodeId = value?.nodeId

    const loop = useMemo(
        () => ({
            open: true,
            name: nodeName
        }),
        [nodeName]
    )

    const parentNodes = useMemo(
        () => allParentNodes.filter(item => item.id === nodeId || OUTPUT_SINGLE_DATA_NODES.has(item.data.nodeType)),
        [allParentNodes, nodeId]
    )
    const subProcessArgs = useMemo(() => {
        const workflow = find(item => item.id === subProcessId && item.type === 'subProcess', workflows)
        if (!workflow) {
            return
        }
        const nodeList = workflow.content?.nodes || []
        const variableNode = find(item => item.data.nodeType === 'ARG_TRIGGER', nodeList)
        if (!variableNode) {
            return
        }
        const { config } = variableNode.data as ClickTriggerConfigure | VariableTriggerConfigure
        if (!config || !config?.args) {
            return
        }
        return config.args
    }, [subProcessId, workflows])

    return (
        <>
            <Divider color="var(--color-gray-200)" />
            <Group label="循环的子流程">
                <SubProcessSelector
                    workflows={workflows}
                    value={subProcessId}
                    onChange={val => {
                        onChange?.({ ...(value || {}), subProcessId: val, args: [] })
                    }}
                />
            </Group>

            {subProcessId && subProcessArgs && subProcessArgs.length > 0 && (
                <>
                    <Divider color="var(--color-gray-200)" />
                    <Group label="子流程参数值设置">
                        <SC.Tip>设置子流程的参数值，参数值将会传递到子流程中。</SC.Tip>
                        <Box px={8}>
                            {(() => {
                                const list = (value?.args || []) ?? []
                                const data =
                                    subProcessArgs.map(item => {
                                        const dataItem = find(d => d.id === item.id, list)
                                        return {
                                            ...item,
                                            value: dataItem?.value
                                        }
                                    }) || []
                                return (
                                    <SettingSubProcessContent
                                        data={data}
                                        loop={loop}
                                        allParentNodes={allParentNodes}
                                        parentNodes={parentNodes}
                                        onChange={args => onChange?.({ ...value, args })}
                                    />
                                )
                            })()}
                        </Box>
                    </Group>
                </>
            )}
        </>
    )
}
